import { useEffect, useRef, useState } from "react";
import { http, apiError } from "@/lib/api";
import { X, PaperPlaneTilt, Sparkle, WarningCircle, Robot } from "@phosphor-icons/react";

/**
 * Ask Coach AI — floating chat modal on the client meal page.
 * Clients text a nutrition/habit question and get an on-brand AI answer.
 * Every Q&A logged so Coach Jimmy can review flagged ones later.
 */
export default function AskCoachAI({ open, onClose }) {
  const [messages, setMessages] = useState([]); // [{role, text, id, ts}]
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [dailyUsed, setDailyUsed] = useState(0);
  const [dailyLimit, setDailyLimit] = useState(20);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open || historyLoaded) return;
    (async () => {
      try {
        const { data } = await http.get("/client/coach-ai/history?limit=10");
        const items = (data?.items || []).slice().reverse(); // oldest → newest for chat display
        const msgs = [];
        for (const it of items) {
          msgs.push({ role: "user", text: it.question, id: `${it.id}-q`, ts: it.created_at });
          msgs.push({ role: "assistant", text: it.answer, id: `${it.id}-a`, ts: it.created_at });
        }
        setMessages(msgs);
        setDailyUsed(data?.daily_used || 0);
        setDailyLimit(data?.daily_limit || 20);
      } catch (e) { /* silent — start with empty chat */ }
      finally { setHistoryLoaded(true); }
    })();
  }, [open, historyLoaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const ask = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setErr("");
    const tempId = `pending-${Date.now()}`;
    setMessages((cur) => [...cur, { role: "user", text: q, id: tempId, ts: new Date().toISOString() }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await http.post("/client/coach-ai/ask", { question: q });
      setMessages((cur) => [...cur, {
        role: "assistant",
        text: data.answer,
        id: data.id,
        ts: data.created_at,
      }]);
      setDailyUsed((n) => n + 1);
    } catch (e) {
      setErr(apiError(e, "Coach AI didn't respond — try again."));
      // Roll back the pending user message so they can retry cleanly
      setMessages((cur) => cur.filter((m) => m.id !== tempId));
      setInput(q);
    } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  if (!open) return null;
  const remaining = Math.max(0, dailyLimit - dailyUsed);

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      data-testid="ask-coach-ai-modal"
    >
      <div
        className="bg-[#0F0F0F] border border-[#FF3333]/40 w-full max-w-2xl sm:max-h-[85vh] max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-[#262626]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-[#FF3333]/60 bg-[#FF3333]/10 flex items-center justify-center shrink-0">
              <Robot size={22} weight="fill" className="text-[#FF3333]" />
            </div>
            <div>
              <div className="font-oswald text-lg text-white uppercase tracking-wide">Coach Jimmy AI</div>
              <p className="text-zinc-500 text-xs font-manrope">Instant AI answer · Coach reviews every reply</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white shrink-0"
            data-testid="ask-coach-ai-close"
          >
            <X size={22} weight="bold" />
          </button>
        </div>

        {/* Chat area */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[240px]"
          data-testid="ask-coach-ai-messages"
        >
          {messages.length === 0 && !sending && (
            <div className="text-center py-8">
              <Sparkle size={32} weight="fill" className="text-[#FF3333]/60 mx-auto mb-3" />
              <p className="text-zinc-300 font-manrope mb-2">Ask me anything about your plan.</p>
              <p className="text-zinc-500 text-xs font-manrope max-w-sm mx-auto leading-relaxed">
                Meal swaps · macros · habit questions · training tweaks.<br />
                (Medical stuff — check with your doc, not me.)
              </p>
              <div className="mt-6 flex flex-col gap-2 max-w-sm mx-auto">
                {[
                  "Can I swap chicken for tofu on Tuesday?",
                  "Best snack under 200 kcal with 20g protein?",
                  "Skipped my workout — should I move it to tomorrow?",
                ].map((sample, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(sample)}
                    data-testid={`ask-coach-ai-sample-${i}`}
                    className="text-left text-zinc-400 hover:text-white text-xs font-manrope border border-[#262626] hover:border-[#FF3333]/40 px-3 py-2 transition-colors"
                  >
                    &quot;{sample}&quot;
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-3 py-2 font-manrope text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#FF3333]/15 border border-[#FF3333]/40 text-white"
                    : "bg-[#1A1A1A] border border-[#262626] text-zinc-200"
                }`}
                data-testid={`ask-coach-ai-msg-${m.role}`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-[#1A1A1A] border border-[#262626] text-zinc-500 px-3 py-2 text-sm font-manrope flex items-center gap-2" data-testid="ask-coach-ai-typing">
                <Sparkle size={14} weight="fill" className="animate-pulse text-[#FF3333]" />
                Coach Jimmy AI is thinking…
              </div>
            </div>
          )}
        </div>

        {/* Error */}
        {err && (
          <div className="mx-4 mb-2 border border-[#FF3333]/60 bg-[#FF3333]/10 text-[#FF9999] px-3 py-2 text-xs font-manrope flex items-start gap-2" data-testid="ask-coach-ai-err">
            <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        {/* Composer */}
        <div className="border-t border-[#262626] p-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              maxLength={400}
              placeholder={remaining > 0 ? "Ask Coach Jimmy AI…" : "Daily limit reached — DM Coach directly."}
              disabled={remaining <= 0 || sending}
              className="id-input flex-1 resize-none disabled:opacity-50"
              data-testid="ask-coach-ai-input"
            />
            <button
              onClick={ask}
              disabled={sending || !input.trim() || remaining <= 0}
              className="id-btn-primary shrink-0 disabled:opacity-40"
              data-testid="ask-coach-ai-send"
            >
              <PaperPlaneTilt size={16} weight="fill" />
            </button>
          </div>
          <div className="mt-2 text-[10px] text-zinc-600 font-manrope uppercase tracking-[0.15em] flex justify-between">
            <span>{input.length}/400</span>
            <span>{remaining} / {dailyLimit} questions left today</span>
          </div>
        </div>
      </div>
    </div>
  );
}
